// SPEC-UPDATE-TIPS-V1 (D-661): 更新を待つ間に「使い方の一行」を出す。
//   ★出す場所は更新の入口の下。更新の入口は D-463(SPEC-UPDATE-ENTRY-SINGLE-POINT-V1)で
//     ログイン画面の版パネル 1 つに集約され、常駐バナー(SwUpdateBanner)は撤去済み。
//     spec BACKGROUND_FACTS 一 は撤去前の姿を書いているので、実コードに合わせてここに置く(status_log 参照)。
//   ★Tips は添え物。更新の振る舞いには一切触らない(spec DESIGN 三 / FORBIDDEN)。
//     ・自動 reload を足さない
//     ・更新ボタンのタップ領域を狭めない(ボタンの下に別要素として置く)
//   ★0 件 / 取得失敗のときは何も出さない。エラーも出さない(spec DESIGN 四)。
import { useEffect, useRef, useState } from 'react'
import { fetchActiveTips, trackTipShown } from '../../services/appTips'

// 切替間隔。
//   ★D-661 は 7 秒(ひろ確認済み 2026-08-30)。
//   ★SPEC-UPDATE-TIPS-READABILITY-V1 (D-663): 実機で読み切る前に切り替わるという指摘で 10 秒へ(ひろ 2026-09-08)。
//     初期 Tips の最長は 50 文字で、日本語の黙読 400〜600 字/分だと 5〜7.5 秒かかる。7 秒は下限に張り付いていた。
export const TIP_INTERVAL_MS = 10000

// SPEC-TIPS-USAGE-COUNT-V1 (D-766): 1 回の「更新待ち」で報告する上限。
//   ★放置タブ対策。D-068 の USAGE_DWELL_CLIP_SECONDS(滞在秒の上限)と同じ意図で、
//     こちらは回数に上限を置く。10 秒ごとに切り替わるので 60 回 = 10 分見続けた分まで。
//     そこから先は端末が置きっぱなしになっているだけで、Tips が読まれたわけではない。
export const TIP_SHOWN_REPORT_MAX = 60

/**
 * 開始位置。毎回同じ Tips から始まらないようにする(spec DESIGN 二)。
 *   ★件数が 0 や不正なら 0。rand が 1 を返す実装差でも範囲外にしない。
 */
export function pickStartIndex(count, rand = Math.random) {
  if (!Number.isInteger(count) || count <= 0) return 0
  const raw = Math.floor(Number(rand()) * count)
  if (!Number.isFinite(raw)) return 0
  return Math.min(Math.max(raw, 0), count - 1)
}

/** 次の位置。末尾まで来たら先頭へ戻る。 */
export function nextIndex(i, count) {
  if (!Number.isInteger(count) || count <= 0) return 0
  return (i + 1) % count
}

/**
 * @param {{
 *   active: boolean,                 // 更新が待ち状態か(= 更新の告知が出ているか)
 *   loadFn?: () => Promise<Array>,   // 取得(テストで差し替える)
 *   intervalMs?: number,
 *   random?: () => number,
 *   trackFn?: (tipId: string) => void,   // D-766: 表示回数の記録(テストで差し替える)
 * }} props
 */
export default function UpdateTips({ active, loadFn = fetchActiveTips, intervalMs = TIP_INTERVAL_MS, random = Math.random, trackFn = trackTipShown }) {
  const [tips, setTips] = useState([])
  const [idx, setIdx] = useState(0)

  // ★出す状態になってから取りに行く。ログイン画面を開いただけで毎回 1 回問い合わせない。
  useEffect(() => {
    if (!active) return undefined
    let alive = true
    Promise.resolve()
      .then(loadFn)
      .then(list => {
        if (!alive) return
        const arr = Array.isArray(list) ? list : []
        setTips(arr)
        setIdx(pickStartIndex(arr.length, random))
      })
      .catch(() => { if (alive) setTips([]) })   // ★取得失敗は無音。更新作業を邪魔しない。
    return () => { alive = false }
  }, [active, loadFn, random])

  useEffect(() => {
    // 1 件以下なら切り替える先が無い。タイマーも回さない。
    if (!active || tips.length < 2) return undefined
    const id = setInterval(() => setIdx(i => nextIndex(i, tips.length)), intervalMs)
    return () => clearInterval(id)
  }, [active, tips.length, intervalMs])

  // ★D-766: いま画面に出ている Tip。下の描画と同じ式で 1 度だけ決める(D-616: 同じ判断を 2 度書かない)。
  //   フックは早期 return より前に置く必要があるので、ここで求めて計測と描画の両方で使う。
  const shown = active && tips.length > 0 ? tips[Math.min(idx, tips.length - 1)] : null
  const shownTipId = shown?.body ? (shown.tipId ?? null) : null

  const reportedRef = useRef(0)
  useEffect(() => {
    if (!shownTipId) return
    if (reportedRef.current >= TIP_SHOWN_REPORT_MAX) return
    reportedRef.current += 1
    // ★fire-and-forget。await しない。
    //   ★呼び先(trackTipShown)も投げない作りだが、それに頼らずここでも囲む(D-524 と同じ二重の囲い)。
    //     計測が更新導線を止めるのが D-661 の最優先事項に反する、というのが理由。
    try {
      const r = trackFn(shownTipId)
      if (r && typeof r.catch === 'function') r.catch(() => {})
    } catch {
      /* 計測は更新作業を止めない */
    }
  }, [shownTipId, trackFn])

  if (!shown?.body) return null

  const tip = shown

  return (
    <div
      data-testid="update-tips"
      role="status"
      aria-live="polite"
      // SPEC-UPDATE-TIPS-READABILITY-V1 (D-663): 実機で読めなかった(ひろ 2026-09-08 22:43)。
      //   ★色は D-647 で採用済みの amber-400(#fbbf24)。新しい黄色を発明しない
      //     (D-548 の失敗 = 淡い色を選んで背景と混ざる、を繰り返さない)。
      //     背景 #0a0a0f に対するコントラスト比は 11.83:1(旧 #9090a8 は 6.34:1)。実測値は status_log。
      //   ★大きさは 12 → 15px。この画面は待ち時間に読むもので、他の要素と競合していない。
      //   ★maxWidth は 300 → 340。大きくすると行数が増えて下の案内と build 行を押し下げるので、
      //     行数が増えないぶんだけ幅を広げる(390 幅の画面に収まる範囲)。
      style={{
        marginTop: 6, maxWidth: 340, textAlign: 'center',
        fontSize: 15, lineHeight: 1.5, color: '#fbbf24', fontWeight: 700,
      }}
    >
      <span data-testid="update-tips-body">{tip.body}</span>
    </div>
  )
}
